import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import css from './tasks.css';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {useSelector} from 'react-redux';
import {selectUserInfo} from 'src/selectors/crm';
import {
  getDashboardTasks,
  INITIAL_APPLICANT_LIST_FILTER_DATA,
  OFFER_STAGE_TASKS,
} from 'src/components/crm/constant';
import {Separator} from '@spaced-out/ui-design-system/lib/components/Separator';
import {
  SubTitleSmall,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Icon,
  SemanticIcon,
} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Badge} from '@spaced-out/ui-design-system/lib/components/Badge';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';
import {borderWidthPrimary} from '@spaced-out/ui-design-system/lib/styles/variables/_border';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import {checkIfIntegrationTypeSF} from 'src/utils/crm-new/common';

import {useHistory} from 'src/rerouter/index';


type TaskConfig = {
  iconName?: string;
  label?: string;
  sortOrder?: number;
};

type TaskData = {
  stageActionStatus: string;
  milestoneId: string;
  count: number;
  jobId: number;
  jobTitle: string;
};

type TasksProps = {
  tasksData: Array<TaskData> | null;
  isLoading: boolean;
  isDashBoardPage: boolean;
};

type AggregatedTask = TaskData & {
  milestoneId: string;
};

type JobsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  tasks: Array<TaskData> | null;
  taskType: string;
};

type TasksConstant = {
  [key: string]: TaskConfig;
};

export const Tasks = ({
  tasksData = [],
  isLoading,
  isDashBoardPage,
}: TasksProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const DASHBOARD_TASKS = getDashboardTasks(t);
  const history = useHistory();
  const [isJobsModalOpen, setJobsModalOpen] = React.useState<boolean>(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const [taskType, setTaskType] = React.useState('');
  const {organization: organizationInfo} = useSelector(selectUserInfo) ?? {};

  const aggregatedTasks = (Array.isArray(tasksData) ? tasksData : [])
    .filter((task) => DASHBOARD_TASKS[task.stageActionStatus])
    .filter(
      (task) =>
        !checkIfIntegrationTypeSF(organizationInfo?.integrationType) ||
        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"GENERATE_OFFER" | "OFFER_ACCEPTED_BY_CANDIDATE" | "OFFER_SENT_TO_CANDIDATE" | "REVIEW_OFFER" | "SEND_OFFER_TO_CANDIDATE" | "REQUEST_FOR_OFFER_APPROVAL" | "REQUESTED_FOR_APPROVAL"'.
        !OFFER_STAGE_TASKS.includes(task.stageActionStatus),
    )
    .reduce<Array<any>>((acc: Array<AggregatedTask>, task: TaskData) => {
      const existingTask = acc.find(
        (t) => t.stageActionStatus === task.stageActionStatus,
      );

      if (existingTask) {
        existingTask.count += task.count;
        existingTask.milestoneId = `${existingTask.milestoneId},${task.milestoneId}`;
      } else {
        acc.push({...task});
      }
      return acc;
    }, []);

  const sortedPendingTasks: Array<AggregatedTask> = aggregatedTasks.sort(
    (a: AggregatedTask, b: AggregatedTask) => {
      const taskA: TaskConfig = DASHBOARD_TASKS[a.stageActionStatus];
      const taskB: TaskConfig = DASHBOARD_TASKS[b.stageActionStatus];
      const orderA: number = taskA.sortOrder ?? Infinity;
      const orderB: number = taskB.sortOrder ?? Infinity;
      return orderA - orderB;
    },
  );

  const totalValidTasks = sortedPendingTasks.reduce(
    (sum, task) => sum + task.count,
    0,
  );

  return (
    <>
      <Card classNames={{wrapper: css.card}}>
        <CardHeader>
          <CardTitle className={css.cardTitleContainer}>
            <SemanticIcon
              name="check"
              semantic="neutral"
              type="solid"
              size="small"
            />
            <SubTitleSmall>{t('All_Tasks', 'All Tasks')}</SubTitleSmall>
            {sortedPendingTasks.length > 0 && (
              <Badge text={String(totalValidTasks)} fill="blue" />
            )}
          </CardTitle>
        </CardHeader>
        {isLoading ? (
          <div className={css.loaderContainer}>
            <CircularLoader size="large" />
          </div>
        ) : (
          <CardContent className={css.innerCard}>
            {sortedPendingTasks.length > 0 ? (
              <div className={css.tasksWrapper}>
                {sortedPendingTasks.map((task: AggregatedTask, index) => {
                  const taskConfig: TaskConfig =
                    DASHBOARD_TASKS[task.stageActionStatus];
                  return (
                    <>
                      <div
                        className={css.taskRowSpaceBetween}
                        key={`${task.stageActionStatus}-${task.count}`}
                        onClick={() => {
                          {
                            isDashBoardPage && setJobsModalOpen(true);
                          }
                          setModalTitle(
                            taskConfig.label || task.stageActionStatus,
                          );
                          setTaskType(task.stageActionStatus);
                        }}
                      >
                        <div className={css.taskRow}>
                          <Icon
                            color="clickable"
                            name={taskConfig.iconName || 'circle'}
                          />
                          <div className={css.taskInfo}>
                            <BodyMedium
                              // @ts-expect-error - TS2769 - No overload matches this call.
                              onClick={() => {
                                if (isDashBoardPage) {
                                  return;
                                }
                                const routeParams = new URLSearchParams();
                                routeParams.set(
                                  'applicantFilters',
                                  JSON.stringify({
                                    ...INITIAL_APPLICANT_LIST_FILTER_DATA,
                                    filter: {
                                      ...INITIAL_APPLICANT_LIST_FILTER_DATA.filter,
                                      stepStatuses: [task.stageActionStatus],
                                    },
                                  }),
                                );
                                history.push(
                                  `/crm/job/${
                                    task.jobId
                                  }/info/applicants?${routeParams.toString()}`,
                                );
                              }}
                            >
                              {taskConfig.label || task.stageActionStatus}
                            </BodyMedium>
                            <Badge text={String(task.count)} fill="blue" />
                          </div>
                        </div>
                        <Button
                          size="medium"
                          type="ghost"
                          iconLeftName="chevron-right"
                          onClick={() => {
                            if (isDashBoardPage) {
                              return;
                            }
                            const routeParams = new URLSearchParams();
                            routeParams.set(
                              'applicantFilters',
                              JSON.stringify({
                                ...INITIAL_APPLICANT_LIST_FILTER_DATA,
                                filter: {
                                  ...INITIAL_APPLICANT_LIST_FILTER_DATA.filter,
                                  stepStatuses: [task.stageActionStatus],
                                },
                              }),
                            );
                            history.push(
                              `/crm/job/${
                                task.jobId
                              }/info/applicants?${routeParams.toString()}`,
                            );
                          }}
                        />
                      </div>
                      {sortedPendingTasks.length - 1 !== index && (
                        <Separator
                          classNames={{
                            wrapper: css.seprator,
                          }}
                          height={borderWidthPrimary}
                          orientation="horizontal"
                          width={sizeFluid}
                        />
                      )}
                    </>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                description={`${t(
                  'No_tasks_pending_Enjoy_the_break',
                  'No tasks pending. Enjoy the break',
                )}!`}
                imageVariant="data"
              />
            )}
          </CardContent>
        )}
      </Card>
      {isJobsModalOpen && (
        <JobsModal
          isOpen={true}
          onClose={() => setJobsModalOpen(false)}
          title={modalTitle}
          tasks={tasksData}
          taskType={taskType}
        />
      )}
    </>
  );
};

const JobsModal = ({
  isOpen,
  onClose,
  title,
  tasks = [],
  taskType,
}: JobsModalProps) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const DASHBOARD_TASKS = getDashboardTasks(t);
  const history = useHistory();
  const [searchJobTextOrCode, setSearchJobTextOrCode] = React.useState('');
  const [selectedJobId, setSelectedJobId] = React.useState(0);

  const options = React.useMemo(() => {
    if (!tasks || (Array.isArray(tasks) && tasks.length === 0)) {
      return [];
    }
    const filteredTasks = tasks.filter((task) => {
      if (!task || !task.stageActionStatus) {
        return false;
      }

      const taskConfig: TaskConfig | null | undefined =
        DASHBOARD_TASKS[task.stageActionStatus];

      if (!taskConfig) {
        return false;
      }

      return (taskConfig.label || task.stageActionStatus) === title;
    });

    const jobMap = new Map();
    filteredTasks.forEach((task) => {
      if (!task || !task.jobId || !task.jobTitle) {
        return;
      }

      const existingJob = jobMap.get(String(task.jobId));
      if (existingJob) {
        jobMap.set(String(task.jobId), {
          ...existingJob,
          count: existingJob.count + task.count,
        });
      } else {
        jobMap.set(String(task.jobId), {
          key: String(task.jobId),
          label: task.jobTitle,
          count: task.count,
        });
      }
    });

    return Array.from(jobMap.values())
      .map((job) => {
        if (!job) {
          return null;
        }
        return {
          key: String(job.key),
          label: `${job.label} (${job.count})`,
        };
      })
      .filter(Boolean);
  }, [tasks, title]);

  const handleProceed = () => {
    const routeParams = new URLSearchParams();
    routeParams.set(
      'applicantFilters',
      JSON.stringify({
        ...INITIAL_APPLICANT_LIST_FILTER_DATA,
        filter: {
          ...INITIAL_APPLICANT_LIST_FILTER_DATA.filter,
          stepStatuses: [taskType],
        },
      }),
    );
    history.push(
      `/crm/job/${selectedJobId}/info/applicants?${routeParams.toString()}`,
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      classNames={{content: css.modalContent}}
    >
      <ModalHeader onCloseButtonClick={onClose}>{title}</ModalHeader>
      <ModalBody className={css.modalBody}>
        <SimpleTypeahead
          label={t('Select_a_Job', 'Select a Job')}
          placeholder={t('Select_or_Search_a_Job', 'Select or Search a Job')}
          typeaheadInputText={searchJobTextOrCode}
          // @ts-expect-error - TS2769 - No overload matches this call.
          options={options}
          onSelect={(option) => {
            // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<number>'.
            setSelectedJobId(option.key);
          }}
          menuOpenOffset={0}
        />
      </ModalBody>
      <ModalFooter classNames={{wrapper: css.footerRadius}}>
        <Button type="ghost" onClick={onClose}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button type="primary" onClick={handleProceed}>
          {t('Proceed', 'Proceed')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
